import {
  validateImageQuality,
} from '@autofact-lib/autofact-lib-check-image-quality';
import config from '../../config/config';

class AutoCaptureHelper {
  MIN_CEDULA_AREA_RATIO = 0.2;
  MIN_ASPECT_RATIO = 1.5;
  MAX_ASPECT_RATIO = 2.5
    
constructor (webcamRef, feedbackCallback, captureCallback, sideCI) {
    this.canvasRef = document.createElement ('canvas');
    this.webcamRef = webcamRef;
    this.feedbackCallback = feedbackCallback;
    this.captureCallback = captureCallback; // Callback para retornar la imagen capturada
    this.referenceMats = [];
    this.capturing = true;
    this.validDetections = 0;
    this.opencvLoaded = false;
    this.qualityResult = {};
    this.isBackSideCI = sideCI == 'back';
  }

  async loadOpenCV () {
    return new Promise ((resolve, reject) => {
      if (window.cv) {
        this.opencvLoaded = true; // OpenCV ya estaba cargado
        resolve ();
      } else {
        const script = document.createElement ('script');
        script.src = 'https://docs.opencv.org/3.4.0/opencv.js';
        script.async = true;
        script.onload = () => {
          console.log ('OpenCV.js loaded');
          this.opencvLoaded = true;
          resolve ();
        };
        script.onerror = () => {
          reject ('Error loading OpenCV.js');
        };
        document.body.appendChild (script);
      }
    });
  }

  loadReferenceImages (images) {
    images.forEach (src => {
      const img = new Image ();
      img.src = src;
      img.onload = () => {
        const canvas = document.createElement ('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const context = canvas.getContext ('2d');
        context.drawImage (img, 0, 0);
        const mat = cv.imread (canvas);
        this.referenceMats.push (mat);
      };
    });
  }

  async startDetectionLoop () {
    const loop = async () => {
      await this.detectIdCard ();
      if (this.capturing) {
        requestAnimationFrame (loop);
      }
    };
    requestAnimationFrame (loop);
  }

  stopDetection () {
    this.capturing = false;
    this.updateFeedback (false);
  }

  getFrame(){
    const video = this.webcamRef.current ? this.webcamRef.current.video : null;
    
    if(!video){
      return {video, src: null, canvas: null};
    }
    const canvas = this.canvasRef;
    const context = canvas.getContext ('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage (video, 0, 0, canvas.width, canvas.height);

    const src = cv.imread(canvas);

    return {video, src, canvas}
  }

  async detectIdCard() {
    const { video, src, canvas } = this.getFrame();

    if (!this.capturing || !video || !this.referenceMats.length) return;
    await this.processAndDetect(src, canvas);
  }

  async processAndDetect(src, canvas, onlyCheck = false) {
    const processedImage = this.processImage(src);
    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(
      processedImage.edges,
      contours,
      hierarchy,
      cv.RETR_EXTERNAL,
      cv.CHAIN_APPROX_SIMPLE
    );

    if(onlyCheck){
      return contours
    }

    let validDetection = await this.detectContours(contours, src, canvas);

    this.updateFeedback (validDetection);
    this.clearOpenCVResources (
      src,
      processedImage.gray,
      processedImage.equalized,
      processedImage.blurred,
      processedImage.edges,
      processedImage.kernel,
      contours,
      hierarchy
    );
  }

  processImage (src) {
    const gray = new cv.Mat ();
    cv.cvtColor (src, gray, cv.COLOR_RGBA2GRAY);

    const equalized = new cv.Mat ();
    cv.equalizeHist (gray, equalized);

    const blurred = new cv.Mat ();
    const ksize = new cv.Size (7, 7);
    cv.GaussianBlur (equalized, blurred, ksize, 0);

    const edges = new cv.Mat ();
    cv.Canny (blurred, edges, 50, 150);

    const kernel = cv.getStructuringElement (cv.MORPH_RECT, new cv.Size (3, 3));
    cv.morphologyEx (edges, edges, cv.MORPH_CLOSE, kernel);

    return {gray, equalized, blurred, edges, kernel};
  }

  async detectContours (contours, src, canvas) {
    let validDetection = false;
    const frameArea = canvas.width * canvas.height;

    for (let i = 0; i < contours.size (); i++) {
      const cnt = contours.get (i);
      const rect = cv.boundingRect (cnt);
      const aspectRatio = rect.width / rect.height;
      const cardArea = rect.width * rect.height;
      const cardAreaRatio = cardArea / frameArea;

      if (
        aspectRatio > this.MIN_ASPECT_RATIO && aspectRatio < this.MAX_ASPECT_RATIO && cardAreaRatio >= this.MIN_CEDULA_AREA_RATIO
      ) {
        const matched = this.matchTemplate(src, rect);
        if (matched) {
          validDetection = await this.validateCroppedImageQuality(src, rect);
          if (validDetection) {
            break;
          }
        }
      }
    }
    return validDetection;
  }

  matchTemplate (src, rect) {
    let validDetection = false;
    const roi = src.roi (rect);
    const result = new cv.Mat ();
    cv.imshow (this.canvasRef, roi);

    const sharpnessThreshold = config.customThresholds.laplacian * 1000;
    const sharpness = this.analyzeSharpness(roi);
    if ( sharpness > sharpnessThreshold) {
      for (let refMat of this.referenceMats) {
        const resizedRoi = new cv.Mat ();
        const size = new cv.Size (refMat.cols, refMat.rows);
        cv.resize (roi, resizedRoi, size);

        cv.matchTemplate (resizedRoi, refMat, result, cv.TM_CCOEFF_NORMED);
        const minMax = cv.minMaxLoc (result);
        if (minMax.maxVal > 0.2) {
          validDetection = true;
          break;
        }
        this.clearOpenCVResources(resizedRoi);
      }
      this.clearOpenCVResources(roi, result)
    }

    return validDetection;
  }

  async validateCroppedImageQuality (src, rect) {
    const roiCanvas = document.createElement ('canvas');
    roiCanvas.width = rect.width;
    roiCanvas.height = rect.height;
    const roiContext = roiCanvas.getContext ('2d');
    const roiImage = src.roi (rect);
    cv.imshow (roiCanvas, roiImage);

    const image = new Image ();
    image.src = roiCanvas.toDataURL ('image/jpeg');
    
    return new Promise (resolve => {
      image.onload = async () => {
        const qualityResult = await validateImageQuality (image, {
          customThresholds: config.customThresholds,
        });
        if (!qualityResult.brightness) {
          this.feedbackCallback (
            'Tienes poca luz, mejora la iluminación.',
            'red'
          );
          return resolve (false);
        }
        if (!qualityResult.contrast) {
          this.feedbackCallback (
            'Tienes mucha luz, disminuye la iluminación.',
            'red'
          );
          return resolve (false);
        }
        if (!qualityResult.sharpness) {
          this.feedbackCallback (
            'La imagen está desenfocada, mueve el teléfono hasta mejorarla.',
            'yellow'
          );
          return resolve (false);
        }
        if (!qualityResult.readableQrCode && this.isBackSideCI) {
          this.feedbackCallback (
            'El QR no es legible. Ajusta la posición.',
            'yellow'
          );
          return resolve (false);
        }

        this.qualityResult = qualityResult;
        resolve (true);
      };
    });
  }

  updateFeedback (validDetection) {
    if (!this.capturing) {
      this.feedbackCallback (
        'Autocaptura desactivada, favor usa el botón para capturar la imagen.',
        'white'
      );
      return;
    }

    let iconColor = 'white';
    let feedbackMessage = '';
    
    if (validDetection) {
      this.validDetections++;
      if (this.validDetections >= 2) {
        iconColor = 'blue';
        const image = new Image ();
        image.src = this.canvasRef.toDataURL ('image/jpeg');
        image.onload = () => {
          this.captureImage (image);
        };
      } else {
        iconColor = 'blue';
        feedbackMessage = 'Mantén esta posición.';
      }
    } else {
      iconColor = 'yellow';
      feedbackMessage =
        'El fondo que utilizas es muy complejo, busca un fondo más liso';
    }

    this.feedbackCallback (feedbackMessage, iconColor);
  }

  captureImage (image) {
    const imageSrc = image
      ? image.src
      : this.webcamRef.current.getScreenshot ();
    if (imageSrc) {
      this.feedbackCallback ('Imagen capturada', 'blue');
      this.captureCallback (imageSrc, this.qualityResult); // Llamamos al callback para retornar la imagen capturada
    } else {
      console.error ('No se pudo capturar la imagen');
    }
  }

  clearOpenCVResources (...mats) {
    mats.forEach (mat => mat.delete ());
  }

  async selectBestCamera(backCameras, timeout = 5000) {
    let bestCamera = null;
    let bestScore = -Infinity;
    for (const device of backCameras) {
      const score = await this.testCamera(device, timeout);
      if (score && score > bestScore) {
        bestScore = score;
        bestCamera = device.deviceId;
      }
    }

    if (bestCamera) {
      return bestCamera
    } else {
      throw new Error("No se pudo seleccionar una cámara adecuada.");
    }
  }

  async testCamera(device, timeout) {
    const controller = new AbortController();
    const signal = controller.signal;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: device.deviceId }, signal });
      this.webcamRef.current.srcObject = stream;

      const { src } = this.getFrame();

      const sharpness = this.analyzeSharpness(src);
      const noiseLevel = this.analyzeNoiseLevel(src);
      const { meanBrightness, contrast } = this.analyzeBrightnessContrast(src);

      const contours = await this.processAndDetect(src, this.webcamRef.current, true);
      const { validRectangles, avgRectangleSize, avgPerspectiveRatio } = this.analyzeRectangles(contours);

      const metrics = { sharpness, noiseLevel, validRectangles, avgRectangleSize, meanBrightness, contrast, avgPerspectiveRatio };
      const score = this.scoreCamera(metrics);

      this.clearOpenCVResources(src, contours)
      clearTimeout(timeoutId);

      return score;
    } catch (error) {
      console.log(`Error al probar cámara ${device.label}:`+ error.stack);
      clearTimeout(timeoutId);
      return null;
    }
  }

  analyzeBrightnessContrast(src) {
    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
  
    const matVector = new cv.MatVector();
    matVector.push_back(gray);
  
    const hist = new cv.Mat();
    const histSize = [256];
    const ranges = [0, 256];
  
    cv.calcHist(matVector, [0], new cv.Mat(), hist, histSize, ranges);
  
    let min = 0, max = 0;
    for (let i = 0; i < 256; i++) {
      const value = hist.data32F[i];
      if (value > 0) {
        if (min === 0) min = i;
        max = i;
      }
    }
  
    const meanBrightness = cv.mean(gray)[0];
    const contrast = max - min;
    this.clearOpenCVResources(gray, matVector, hist)
  
    return { meanBrightness, contrast };
  }

  analyzeNoiseLevel(src) {
    const blurred = new cv.Mat();
    cv.GaussianBlur(src, blurred, new cv.Size(5, 5), 0);
  
    const noise = new cv.Mat();
    cv.absdiff(src, blurred, noise);
  
    const noiseLevel = cv.mean(noise)[0];
  
    this.clearOpenCVResources(blurred, noise)
    return noiseLevel;
  }

  analyzeSharpness(src) {
    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
  
    const laplacian = new cv.Mat();
    cv.Laplacian(gray, laplacian, cv.CV_8U);
  
    const meanStdDev = new cv.Mat();
    const stddev = new cv.Mat ();
    cv.meanStdDev(laplacian, meanStdDev, stddev);
  
    const sharpness = stddev.data64F[0];
    
    this.clearOpenCVResources(gray, laplacian, meanStdDev, stddev)
  
    return sharpness;
  }

  analyzePerspectiveRatio(rect) {
    const width = rect.width;
    const height = rect.height;
    const perspectiveRatio = Math.abs(width / height - 1);
  
    return perspectiveRatio;
  }

  analyzeRectangles(contours) {
    let totalArea = 0;
    let validRectangles = 0;
    let totalPerspectiveRatio = 0;
  
    for (let i = 0; i < contours.size(); i++) {
      const contour = contours.get(i);
      const approx = new cv.Mat();
      cv.approxPolyDP(contour, approx, 0.02 * cv.arcLength(contour, true), true);
  
      if (approx.rows === 4) {
        const rect = cv.boundingRect(approx);
        totalArea += rect.width * rect.height;
        validRectangles++;
        totalPerspectiveRatio += this.analyzePerspectiveRatio(rect);
      }
      this.clearOpenCVResources(approx);
    }
  
    const avgRectangleSize = validRectangles > 0 ? totalArea / validRectangles : 0;
    const avgPerspectiveRatio = validRectangles > 0 ? totalPerspectiveRatio / validRectangles : 0;
  
    return { validRectangles, avgRectangleSize, avgPerspectiveRatio };
  }

  scoreCamera(metrics) {
    const { sharpness, noiseLevel, validRectangles, avgRectangleSize, avgPerspectiveRatio, meanBrightness, contrast } = metrics;
    
    const sharpnessScore = sharpness * 0.4;
    const noiseScore = noiseLevel * -0.3;
    const rectangleScore = validRectangles * 0.2;
    const avgSizeScore = avgRectangleSize * -0.1;
    const brightnessScore = meanBrightness * 0.1;
    const contrastScore = contrast * 0.1;
    const perspectiveRatioScore = Number(avgPerspectiveRatio) * -0.2;
    
    return (
      sharpnessScore +
      noiseScore +
      rectangleScore +
      avgSizeScore +
      brightnessScore +
      contrastScore +
      perspectiveRatioScore
    );
  }
  
}

export default AutoCaptureHelper;
